import { Component, Input, Output, EventEmitter, CUSTOM_ELEMENTS_SCHEMA, AfterViewInit, ElementRef } from '@angular/core';

import { AuctionEvent, TranslatableString, getTranslation } from '../../../models/auction-event.model';
import 'add-to-calendar-button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LocaleService } from 'src/app/Shared/services/locale/locale.service';

@Component({
    selector: 'app-auction-modal',
        standalone: true,
imports: [TranslateModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './auction-modal.component.html',
    styleUrl: './auction-modal.component.scss'
})
export class AuctionModalComponent implements AfterViewInit {
  @Input() auction!: AuctionEvent;
  @Input() locale: string = 'en';
  @Input() appDir: string = 'ltr';
  @Output() close = new EventEmitter<void>();

  constructor(
    private translate: TranslateService,
    public localeService: LocaleService,
    private elementRef: ElementRef
  ) {}

  ngAfterViewInit() {
    const btn = this.elementRef.nativeElement.querySelector('add-to-calendar-button');
    if (btn) {
      btn.setAttribute('language', this.calendarLanguage);
      if (this.calendarLanguage === 'ar' && btn.whenInitialized) {
        btn.whenInitialized().then(() => {
          this.convertShadowDomNumerals(btn.shadowRoot);
        });
      }
    }
  }

  private convertShadowDomNumerals(shadowRoot: ShadowRoot | null): void {
    if (!shadowRoot) return;
    const dayEl = shadowRoot.querySelector('.atcb-date-btn-day');
    if (dayEl?.textContent) {
      dayEl.textContent = this.toEasternArabicNumerals(dayEl.textContent.trim());
    }
  }

  private toEasternArabicNumerals(str: string): string {
    const numerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return str.replace(/[0-9]/g, (d) => numerals[parseInt(d)]);
  }

  onClose() {
    this.close.emit();
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  openAuctionUrl() {
    if (this.auction.auctionUrl) {
      window.open(this.auction.auctionUrl, '_blank');
    }
  }

  getCalendarStartDate(): string {
    // Convert auction date to YYYY-MM-DD format
    const d = this.auction.date;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getCalendarEndDate(): string {
    // For now, use same date as start (single day event)
    // Future enhancement: parse endDate string if different from startDate
    return this.getCalendarStartDate();
  }

  getCalendarDescription(): string {
    // Build description with auction details (using translated content)
    let description = this.getTranslatedField(this.auction.description);

    if (this.auction.auctionUrl) {
      description += `\n\nView Auction: ${this.auction.auctionUrl}`;
    }

    if (this.auction.consignor) {
      const consignor = this.getTranslatedField(this.auction.consignor);
      description += `\n\nConsignor: ${consignor}`;
    }

    if (this.auction.email) {
      description += `\n\nEmail: ${this.auction.email}`;
    }

    if (this.auction.phone) {
      description += `\nPhone: ${this.auction.phone}`;
    }

    return description;
  }

  /**
   * Get translated text from a translatable field
   */
  getTranslatedField(field: TranslatableString | string | undefined): string {
    return getTranslation(field, this.localeService.languageCode);
  }

  /**
   * Format date string according to current locale
   * Handles both single dates and date ranges (e.g., "February 12, 2026 - February 13, 2026")
   */
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';

    try {
      const locale = this.localeService.locale;
      const dateOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };

      // Check if it's a date range (contains " - ")
      if (dateString.includes(' - ')) {
        const [startStr, endStr] = dateString.split(' - ');
        const startDate = new Date(startStr.trim());
        const endDate = new Date(endStr.trim());

        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const formattedStart = startDate.toLocaleDateString(locale, dateOptions);
          const formattedEnd = endDate.toLocaleDateString(locale, dateOptions);
          return `${formattedStart} - ${formattedEnd}`;
        }
        return dateString;
      }

      // Single date
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString(locale, dateOptions);
    } catch {
      return dateString;
    }
  }

  /**
   * Format time string according to current locale
   * Handles time formats like "2:00 PM", "14:00", etc.
   */
  formatTime(timeString: string | null | undefined): string {
    if (!timeString) return '';

    try {
      const locale = this.localeService.locale;

      // Try to parse the time string
      // Create a date object with today's date and the given time
      const today = new Date();
      const dateTimeStr = `${today.toDateString()} ${timeString}`;
      const date = new Date(dateTimeStr);

      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString(locale, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
      }

      // If parsing fails, return original string
      return timeString;
    } catch {
      return timeString;
    }
  }

  // Translation getter methods
  get consignorLabel(): string {
    return this.translate.instant('auctions_calendar.modal.consignor');
  }

  get emailLabel(): string {
    return this.translate.instant('auctions_calendar.modal.email');
  }

  get phoneLabel(): string {
    return this.translate.instant('auctions_calendar.modal.phone');
  }

  get locationLabel(): string {
    return this.translate.instant('auctions_calendar.modal.location');
  }

  get previewDateLabel(): string {
    return this.translate.instant('auctions_calendar.modal.preview_date');
  }

  get auctionTimeLabel(): string {
    return this.translate.instant('auctions_calendar.modal.auction_time');
  }

  get startDateLabel(): string {
    return this.translate.instant('auctions_calendar.modal.start_date');
  }

  get endDateLabel(): string {
    return this.translate.instant('auctions_calendar.modal.end_date');
  }

  get descriptionLabel(): string {
    return this.translate.instant('auctions_calendar.modal.description');
  }

  get addToCalendarLabel(): string {
    return this.translate.instant('auctions_calendar.modal.add_to_calendar');
  }

  get addToCalendarButtonLabel(): string {
    return this.translate.instant('auctions_calendar.modal.add_to_calendar_button');
  }

  get closeLabel(): string {
    return this.translate.instant('auctions_calendar.modal.close');
  }

  get openAuctionLabel(): string {
    return this.translate.instant('auctions_calendar.modal.open_auction');
  }

  get calendarLanguage(): string {
    return this.locale ? this.locale.split('-')[0] : 'en';
  }
}
